import schoolListingsResultsHandler from 'app-requests/clickPortalSchoolManager';
import { SCHOOL_IMPRESSION_TYPES } from 'consts';
import {
  DomainLayout,
  ErrorPriority,
  MultiRowSchoolListingResults,
  RedisHelpers,
  ClickPortalResultRowsSectionProps,
  ResultRow,
} from 'types';
import { LogError } from 'utils/logging/LogError';
import { transformResultRowsConfigToSchoolFilters } from 'utils/transformResultRowsConfigToSchoolFilters';

interface MetaData {
  urlQuery: Record<string, unknown>;
  pageLayoutInfo: { frontendUrl: string };
  domainLayout: DomainLayout;
  redisHelpers: RedisHelpers;
}

function transformResultRows(resultRows: ResultRow[]): ResultRow[] {
  return resultRows.map((row) => {
    if (row.resultType === 'filterKey') {
      return {
        title: row.title || '',
        subTitle: row.subTitle || '',
        resultType: 'filterKey',
        filterKey: row.filterKey || '',
        filters: { degreeType: null, parentCategory: null, category: null },
      };
    }

    return {
      title: row.title || '',
      subTitle: row.subTitle || '',
      resultType: 'filters',
      filterKey: '',
      filters: {
        degreeType: row.filters?.degreeType || null,
        parentCategory: row.filters?.parentCategory || null,
        category: row.filters?.category || null,
      },
    };
  });
}

// Can we just used the ... endpoint directly? Does this ship to client?
export default async function wpToClickPortalResultRowsSectionTransform(
  acfValues: ClickPortalResultRowsSectionProps,
  metaData: MetaData
): Promise<ClickPortalResultRowsSectionProps> {
  const { urlQuery, pageLayoutInfo, domainLayout, redisHelpers } = metaData;
  const resultRowsConfig = transformResultRows(
    acfValues.resultRowsConfig || []
  );

  let initalResults: MultiRowSchoolListingResults | undefined;

  try {
    let _queryParams: { name: string; value: unknown }[] | undefined;

    if (urlQuery && Object.keys(urlQuery).length > 0) {
      _queryParams = Object.keys(urlQuery).map((key) => ({
        name: key,
        value: Array.isArray(urlQuery[key])
          ? urlQuery[key].join(',')
          : urlQuery[key],
      }));
    }

    const resultsRequest = {
      requestRows: transformResultRowsConfigToSchoolFilters(resultRowsConfig),
      impressionType: SCHOOL_IMPRESSION_TYPES.CP_REQUEST_IMPRESSIONS_CACHE_KEY,
      isMultiRow: true,
      isPersonalized: false,
      geoLocation: {},
      isCurated: acfValues.isCurated || false,
      cookies: {},
      originalUrl: `${pageLayoutInfo.frontendUrl}?server-component=true`,
      queryParams: _queryParams,
      schoolCode: domainLayout?.meta?.schoolCode,
      siteType: domainLayout?.meta?.siteType,
    };

    initalResults = await redisHelpers.getMultiSchoolListings({
      body: resultsRequest,
    });

    if (!initalResults || initalResults.resultRows.length === 0) {
      const { requestRows, siteType, ...rest } = resultsRequest;
      initalResults = await schoolListingsResultsHandler(
        resultsRequest.requestRows,
        resultsRequest.siteType,
        /* sessionId */ undefined,
        { ...rest, queryParams: urlQuery }
      );

      redisHelpers.setMultiSchoolListings(
        { body: resultsRequest },
        initalResults
      );
    }
  } catch (error) {
    LogError('Error fetching click portal result rows from pageSection', {
      priority: ErrorPriority.ONE,
      description: (error as Error).message,
    });
  }

  return {
    background: acfValues.background || '',
    variation: acfValues.variation || '',
    description: acfValues.description || '',
    customCssClass: acfValues.customCssClass || '',
    isReversed: acfValues.isReversed || '',
    textAlignment: acfValues.textAlignment || '',
    title: acfValues.title || '',
    mobileTitle: acfValues.mobileTitle || '',
    subTitle: acfValues.subTitle || '',
    isCurated: acfValues.isCurated || false,
    resultRowsConfig,
    initalResults: initalResults
      ? {
          ...initalResults,
          resultRows: [initalResults.firstRow],
        }
      : null,
  };
}
